import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { $crud } from "../helper/CrudFactory";
import LanguageContext from "../helper/LanguageContext";
import { SettingsBanner, SettingsNote, SettingsRow, SettingsSection, SettingsTag } from "./settings/SettingsList";
import {
  loadTodoistAccounts,
  getTodoistOAuthUrl,
  syncTodoistAccount,
  disconnectTodoistAccount,
} from "../helper/todoistApi";
import "./TodoistSyncSettings.css";

/**
 * Todoist (mobile TodoistSettingsPro): two-way sync with the Todoist project "Kjuup".
 * Shown beside calendar sync, with the same plan lock.
 */
export default function TodoistSyncSettings({
  strings = {},
  locked = false,
}) {
  const { isGerm } = React.useContext(LanguageContext);
  const tr = (key, de, en) => strings[key] || (isGerm ? de : en);
  const subscription = useSelector((s) => s.subscription) || {};
  const isKjuupPlusMember = !!subscription.isKjuupPlusMember;
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const oauthTimerRef = useRef(null);

  const t = (key, fallback) => strings[key] || fallback;

  const fetchAccounts = useCallback(async () => {
    setLoading(true);
    try {
      const list = await loadTodoistAccounts();
      setAccounts(list);
      return list;
    } catch (e) {
      console.error("todoist accounts fetch failed", e);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (locked) return;
    fetchAccounts();
  }, [fetchAccounts, locked]);

  useEffect(() => {
    const onMessage = async (event) => {
      const data = event?.data;
      if (!data || data.type !== "kjuup-todoist-oauth") return;
      if (oauthTimerRef.current) {
        clearInterval(oauthTimerRef.current);
        oauthTimerRef.current = null;
      }
      setConnecting(false);
      if (data.success) {
        await $crud.notify({
          type: "success",
          message: data.message || t("todoist_connected", "Todoist connected"),
        });
        await fetchAccounts();
      } else {
        await $crud.notify({
          type: "error",
          message: data.message || t("todoist_connect_failed", "Todoist connection failed"),
        });
      }
    };
    window.addEventListener("message", onMessage);
    return () => {
      window.removeEventListener("message", onMessage);
      if (oauthTimerRef.current) clearInterval(oauthTimerRef.current);
    };
  }, [fetchAccounts]);

  const connect = async () => {
    if (locked || connecting) return;
    setConnecting(true);
    try {
      const authUrl = await getTodoistOAuthUrl();
      if (!authUrl) {
        setConnecting(false);
        await $crud.notify({
          type: "error",
          message: t("todoist_auth_url_failed", "Could not open the Todoist authorization page."),
        });
        return;
      }
      const popup = window.open(
        authUrl,
        "kjuup-todoist-oauth",
        "width=600,height=700,scrollbars=yes"
      );
      if (!popup) {
        setConnecting(false);
        await $crud.notify({ type: "error", message: "Popup blocked. Please allow popups." });
        return;
      }
      if (oauthTimerRef.current) clearInterval(oauthTimerRef.current);
      oauthTimerRef.current = setInterval(() => {
        if (popup.closed) {
          clearInterval(oauthTimerRef.current);
          oauthTimerRef.current = null;
          setConnecting(false);
          fetchAccounts();
        }
      }, 500);
    } catch (e) {
      setConnecting(false);
      console.error(e);
    }
  };

  const syncNow = async () => {
    if (locked || syncing) return;
    setSyncing(true);
    try {
      const result = await syncTodoistAccount();
      if (!result.ok) {
        await $crud.notify({
          type: "error",
          message: result.message || t("todoist_sync_failed", "Sync failed"),
        });
        await fetchAccounts();
        return;
      }
      await $crud.notify({
        type: "success",
        message: t("todoist_sync_done", "Sync finished"),
      });
      await fetchAccounts();
    } finally {
      setSyncing(false);
    }
  };

  const disconnect = async (account) => {
    try {
      await $crud.confirm({
        title: tr("todoist_disconnect", "Trennen", "Disconnect"),
        textContent: tr(
          "todoist_disconnect_confirm",
          "Todoist trennen? Bereits angelegte Aufgaben bleiben in Todoist. Die Synchronisierung stoppt.",
          "Disconnect Todoist? Tasks already in Todoist stay there. Sync stops."
        ),
        options: {
          ok: tr("todoist_disconnect", "Trennen", "Disconnect"),
          cancel: strings.cancel || (isGerm ? "Abbrechen" : "Cancel"),
        },
      });
    } catch (_) {
      return;
    }
    setBusyId(account.id);
    const result = await disconnectTodoistAccount(account.id);
    setBusyId(null);
    if (!result.ok) {
      await $crud.notify({
        type: "error",
        message: result.message || t("todoist_sync_failed", "Sync failed"),
      });
      return;
    }
    setAccounts((prev) => prev.filter((item) => item.id !== account.id));
    await $crud.notify({
      type: "success",
      message: result.message || t("todoist_disconnected", "Todoist disconnected"),
    });
  };

  const account = accounts[0];

  const formatWhen = (value) => {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    return date.toLocaleString();
  };

  const lastSync = account ? formatWhen(account.lastSyncAt) : "";
  const authError = !!account && account.status === "auth_error";
  const connectLabel = tr("todoist_connect", "Todoist verbinden", "Connect Todoist");
  const connectedLabel = tr("v2_settings_connected", "Verbunden", "Connected");

  return (
    <div className="todoist-sync-settings todo-pro">
      <SettingsNote className="todo-pro__intro">
        {tr(
          "todoist_sync_info",
          "Aufgaben bleiben in deinem Todoist-Projekt „Kjuup“ in beide Richtungen synchron. Eine Aufgabe, die du dort anlegst, wird eine persönliche Kjuup-Aufgabe.",
          "Tasks stay in sync both ways inside your Todoist project named Kjuup. A task you create there becomes a personal Kjuup task."
        )}
      </SettingsNote>

      {locked ? (
        <SettingsBanner
          tone="warn"
          icon="lock"
          title={isKjuupPlusMember
            ? t("kjuup_io_company_managed_body_short", "Ask your company admin to assign you a Kjuup.io seat in the Admin Panel.")
            : t("kjuup_io_trial_ended_body", "Subscribe to Kjuup.io to keep using email and other premium features.")}
        />
      ) : loading && !account ? (
        <div className="kj-settings-card todo-pro__skeleton" aria-busy="true">
          {[0, 1].map((i) => (
            <div key={i} className="todo-pro__bone-row">
              <span className="todo-pro__bone todo-pro__bone--tile" />
              <span className={`todo-pro__bone todo-pro__bone--line${i ? " is-short" : ""}`} />
            </div>
          ))}
        </div>
      ) : !account ? (
        <SettingsSection>
          <SettingsRow
            icon="checklist"
            label={connecting ? t("email_connecting", "Connecting...") : connectLabel}
            busy={connecting}
            onClick={connecting ? undefined : connect}
          />
        </SettingsSection>
      ) : (
        <>
          {authError ? (
            <SettingsBanner
              tone="err"
              icon="sync_problem"
              title={tr("todoist_auth_error", "Verbinde Todoist erneut, um weiter zu synchronisieren.", "Reconnect Todoist to keep syncing.")}
            >
              <button
                type="button"
                className={`kj-btn kj-btn--primary kj-btn--sm${connecting ? " kj-btn--loading" : ""}`}
                disabled={connecting}
                onClick={connect}
              >
                <span className="material-symbols-outlined" aria-hidden="true">link</span>
                <span>{connectLabel}</span>
              </button>
            </SettingsBanner>
          ) : null}
          <SettingsSection caption={connectedLabel}>
            <SettingsRow
              icon="checklist"
              label={account.email}
              sub={lastSync ? `${tr("todoist_last_sync", "Letzte Synchronisierung", "Last sync")}: ${lastSync}` : undefined}
              trailing={account.status === "active" ? <SettingsTag tone="ok">{connectedLabel}</SettingsTag> : null}
            />
            {!authError ? (
              <SettingsRow
                icon="sync"
                label={syncing
                  ? tr("todoist_syncing", "Wird synchronisiert…", "Syncing…")
                  : tr("todoist_sync_now", "Jetzt synchronisieren", "Sync now")}
                busy={syncing}
                chevron={false}
                onClick={syncing ? undefined : syncNow}
              />
            ) : null}
            <SettingsRow
              icon="link_off"
              danger
              label={tr("todoist_disconnect", "Trennen", "Disconnect")}
              busy={busyId === account.id}
              onClick={busyId === account.id ? undefined : () => disconnect(account)}
            />
          </SettingsSection>
        </>
      )}
    </div>
  );
}
